import { useEffect, useState } from "react";

const VISIT_KEY = "syncbook_visit_count";
const LAST_VISIT_KEY = "syncbook_last_visit";
const DISMISSED_KEY = "syncbook_pwa_dismissed_at";
const SESSION_GAP_MIN = 30; // Two visits within 30min count as one session.
const DISMISS_COOLDOWN_DAYS = 14;

/**
 * Increments the visit counter once per browser session
 * (two loads within 30 minutes count as one visit).
 * Returns the current visit count.
 */
export function usePwaVisitTracker() {
  const [count, setCount] = useState(() => {
    if (typeof window === "undefined") return 0;
    return Number(window.localStorage.getItem(VISIT_KEY) || 0);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const now = Date.now();
    const last = Number(window.localStorage.getItem(LAST_VISIT_KEY) || 0);
    const gapMin = (now - last) / 1000 / 60;
    if (last === 0 || gapMin > SESSION_GAP_MIN) {
      const next = Number(window.localStorage.getItem(VISIT_KEY) || 0) + 1;
      window.localStorage.setItem(VISIT_KEY, String(next));
      window.localStorage.setItem(LAST_VISIT_KEY, String(now));
      setCount(next);
    }
  }, []);

  return count;
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = window.navigator && window.navigator.standalone === true;
  return Boolean(mm || iosStandalone);
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ reports Mac; also check touch support.
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
}

export function dismissPwaPrompt() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
}

export function isDismissedRecently() {
  if (typeof window === "undefined") return false;
  const at = Number(window.localStorage.getItem(DISMISSED_KEY) || 0);
  if (!at) return false;
  const days = (Date.now() - at) / 1000 / 60 / 60 / 24;
  return days < DISMISS_COOLDOWN_DAYS;
}

export function resetPwaTracker() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(VISIT_KEY);
  window.localStorage.removeItem(LAST_VISIT_KEY);
  window.localStorage.removeItem(DISMISSED_KEY);
}
